/**
 * Service record → UI type.
 *
 * The service mirrors Bridgify's product catalogue and DELIBERATELY stores no
 * live pricing, timeslots or availability (the integration spec is explicit
 * that the in-cart calls are authoritative). So a `AttractionDetail` built here
 * carries everything the catalogue genuinely knows and leaves the rest empty —
 * the detail page guards each section on content rather than rendering a bare
 * heading, and the booking funnel's ticket shapes come from
 * `booking-shape.ts`, not from this file.
 *
 * Detail content (highlights, gallery, schedule, reviews, the quick-facts
 * strip) DOES have a catalogue source as of 2026-09-22: it is mirrored from
 * Bridgify's `additional_info` block, which the product list payload always
 * carried and the mapper simply never read. Coverage is uneven — highlights
 * 34%, reviews 10%, opening hours ~0%, meeting point 0% — so every section
 * guards on content and hides itself rather than rendering a bare heading.
 *
 * `faqs` is the one field with genuinely NO source anywhere in Bridgify.
 * Do not invent copy for it; a plausible-looking placeholder is worse than an
 * absent section.
 */

import type {
  AttractionDetail,
  AttractionFieldIcon,
  AttractionInfoField,
  AttractionItinerary,
  AttractionScheduleBlock,
  AttractionSummary,
  DurationBucket,
  StartTimeBucket,
} from '../config';
import type { AttractionRecord } from './attractions.api';
import { IMG } from '../../imgPrefix';

/**
 * Shown when a product has no photo at all.
 *
 * Routed through `IMG` like every other static image — and like
 * `SearchCombobox`'s copy of the same fallback — rather than as a bare
 * `/image/…` path. A deployed build is served under a CloudFront path prefix,
 * where a root-relative path resolves against the domain root rather than this
 * app.
 *
 * Exported so `AttractionCard` can tell a real photo from the placeholder and
 * present the two differently; a second literal in the component would drift.
 */
export const PLACEHOLDER_IMAGE = `${IMG}/image/no-image.png`;

/**
 * Bridgify's category vocabulary is plural and broader than the sidebar's.
 * Mapping it explicitly keeps the existing facet checkboxes functional; an
 * unmapped category still flows through verbatim, so `AttractionsList` can
 * offer it as a facet without a code change here.
 */
const CATEGORY_TO_TYPE: Record<string, string> = {
  museums: 'Museum',
  museum: 'Museum',
  castles: 'Castle',
  castle: 'Castle',
  aquariums: 'Aquarium',
  aquarium: 'Aquarium',
  'observation decks': 'Observation Deck',
  'observation deck': 'Observation Deck',
};

/** Categories that read as a FEATURE in the sidebar rather than a place type. */
const CATEGORY_TO_FEATURE: Record<string, string> = {
  'guided tours': 'Guided Tours',
  'guided tour': 'Guided Tours',
  'attraction tickets': 'Entry Tickets',
  'entry tickets': 'Entry Tickets',
  'skip the line': 'Skip the Line',
  'skip-the-line': 'Skip the Line',
};

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

const HTML_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  '#39': "'",
};

/**
 * Supplier prose → plain text.
 *
 * Bridgify descriptions are NOT plain strings: 56% of the live catalogue (279 of
 * a 500-record sample) arrives as rich text — `<p>`, `<br>`, `<strong>`,
 * `<ul>/<li>`, `<a>`, stray `<quillbot-extension-portal>` editor junk — plus
 * `&nbsp;`/`&amp;` entities. Every surface that consumes a description renders
 * it as TEXT (the card teaser, the detail Overview paragraphs, the page
 * `<meta name="description">` and the client-side search haystack), so the raw
 * markup was printed verbatim: "<p>For an AWESOME family day out…</p>".
 *
 * Stripping here rather than in a component keeps the rule in the one module
 * that knows the wire shape — and keeps it out of `dangerouslySetInnerHTML`,
 * which would be an XSS sink for third-party supplier copy.
 *
 * Block boundaries become blank lines so `paragraphs()` can still split the
 * description into real paragraphs (an HTML description has no literal `\n\n`,
 * so without this the whole detail Overview collapsed into one block).
 */
export function stripHtml(input?: string | null): string {
  if (!input) return '';
  return (
    input
      // Never leak script/style bodies into visible copy.
      .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '')
      // `</br>` too: invalid HTML, but supplier ticket copy uses it as a break.
      .replace(/<\/?br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|ul|ol|tr|h[1-6]|blockquote)\s*>/gi, '\n\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&([a-zA-Z]+|#\d+);/g, (match, name: string) => {
        const named = HTML_ENTITIES[name.toLowerCase()];
        if (named !== undefined) return named;
        const numeric = /^#(\d+)$/.exec(name);
        return numeric ? String.fromCharCode(Number(numeric[1])) : match;
      })
      // Collapse runs of spaces/tabs, then runs of blank lines, without
      // flattening the paragraph breaks introduced above.
      .replace(/[^\S\n]+/g, ' ')
      .replace(/[^\S\n]*\n[^\S\n]*/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
}

export function mapTypes(categories: string[] = []): string[] {
  return unique(
    categories.map((c) => CATEGORY_TO_TYPE[c.trim().toLowerCase()] ?? c.trim()).filter(Boolean),
  );
}

export function mapFeatures(record: AttractionRecord): string[] {
  const out: string[] = [];
  for (const c of record.categories ?? []) {
    const mapped = CATEGORY_TO_FEATURE[c.trim().toLowerCase()];
    if (mapped) out.push(mapped);
  }
  if (record.isAccessible) out.push('Access for Disabled');
  return unique(out);
}

/**
 * `duration` arrives as `HH:MM:SS`; `durationLabel` is the human string. Parse
 * the machine one — the label's wording varies per supplier.
 */
export function durationMinutes(duration?: string | null): number | null {
  if (!duration) return null;
  const parts = duration.split(':').map((p) => Number(p));
  if (parts.length < 2 || parts.some((p) => !Number.isFinite(p))) return null;
  const [h, m] = parts;
  return h * 60 + m;
}

/**
 * Local fallback for a record the service has not yet derived fields for.
 *
 * `null` for a missing duration, NOT the widest bucket. The old behaviour
 * bucketed duration-less products as `1dayPlus` to keep a client-side filter
 * from excluding them; now that the facet counts are computed server-side, that
 * would put every product with no duration into the "1 Day+" count and keep the
 * Duration panel visible for a destination where nothing carries a duration at
 * all (BCP-2062 §8 TC6 requires it hidden).
 */
export function mapDurationBucket(record: AttractionRecord): DurationBucket | null {
  const minutes = durationMinutes(record.duration);
  if (minutes === null) return null;
  if (minutes <= 60) return 'upto1h';
  if (minutes <= 240) return '1to4h';
  if (minutes < 24 * 60) return '4hto1d';
  if (minutes === 24 * 60) return '1day';
  return '1dayPlus';
}

/**
 * Start-time buckets need timeslots, which the catalogue does not store — they
 * come from the cart's `timeslots` step, per product. Always empty here;
 * `AttractionsList` hides the facet when nothing carries it, rather than
 * offering a filter that can only ever return zero results.
 */
export function mapStartTimeBuckets(): StartTimeBucket[] {
  return [];
}

/** First sentence (or a clean truncation) for the card teaser. */
export function summarise(description = '', max = 160): string {
  const text = description.replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  const sentenceEnd = text.slice(0, max).lastIndexOf('. ');
  if (sentenceEnd > 60) return text.slice(0, sentenceEnd + 1);
  const wordEnd = text.slice(0, max).lastIndexOf(' ');
  return `${text.slice(0, wordEnd > 0 ? wordEnd : max)}…`;
}

/** Split prose into paragraphs on blank lines, falling back to one block. */
function paragraphs(text?: string | null): string[] {
  if (!text?.trim()) return [];
  return text
    .split(/\n{2,}|\r\n\r\n/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/** Split policy/info prose into lines — these render as a bulleted list. */
function lines(text?: string | null): string[] {
  if (!text?.trim()) return [];
  return text
    .split(/\r?\n|(?<=\.)\s{2,}/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export function toSummary(record: AttractionRecord): AttractionSummary {
  const photos = record.photos?.filter(Boolean) ?? [];
  const description = stripHtml(record.description);
  return {
    id: record.uuid,
    slug: record.slug,
    title: record.title,
    summary: summarise(description),
    description: description.replace(/\s+/g, ' ').trim(),
    image: record.mainPhotoUrl || photos[0] || PLACEHOLDER_IMAGE,
    durationLabel: record.durationLabel ?? '',
    rating: record.rating ?? 0,
    reviewCount: record.numberOfReviews ?? 0,
    // A free product is a real 0, not a missing price. `price: null` means the
    // supplier does not quote until quantities are submitted (a documented
    // Bridgify pattern) — also 0 here, and the card shows no "From" figure.
    fromPrice: record.isFree ? 0 : (record.price ?? 0),
    currency: record.currency ?? 'GBP',
    city: record.city ?? '',
    country: record.country ?? '',
    citySlug: record.citySlug ?? '',
    countrySlug: record.countrySlug ?? '',
    // The SERVICE owns the facet vocabulary now (`facet-taxonomy.ts`): it is
    // what the filter counts are aggregated over and what the panel's
    // checkboxes send back, so deriving a different answer here would offer a
    // value nothing matches. The local mapping stays only as a fallback for a
    // document mirrored before those fields existed — run `POST /sync/scores`
    // and it stops being reachable.
    types: record.types?.length ? record.types : mapTypes(record.categories),
    features: record.features?.length ? record.features : mapFeatures(record),
    durationBucket: (record.durationBucket as DurationBucket | null) ?? mapDurationBucket(record),
    startTimeBuckets: (record.startTimeBuckets as StartTimeBucket[]) ?? mapStartTimeBuckets(),
  };
}

/** Icons arrive as free-form strings on the wire; keep the UI union closed. */
const FIELD_ICONS: AttractionFieldIcon[] = ['clock', 'ticket', 'pin', 'info', 'headphones', 'mic', 'bus'];

function fieldIcon(raw: string | undefined): AttractionFieldIcon {
  return FIELD_ICONS.includes(raw as AttractionFieldIcon) ? (raw as AttractionFieldIcon) : 'info';
}

/**
 * GetYourGuide image URLs end in a rendition id: `…/img/tour/<hash>/<id>.jpg`.
 * Its `main_photo_url` always uses `76` (1920×1280), but `additional_image_urls`
 * use `131` — a 240×160 THUMBNAIL (2,531 of 2,531 live, 2026-09-28). The
 * gallery tiles upscaled those and the lightbox showed them at 240px. Same
 * image, same hash; only the rendition id differs, and the CDN serves every id.
 */
const GYG_RENDITION = /^(https:\/\/cdn\.getyourguide\.com\/img\/tour\/[^/]+)\/(\d+)\.jpg$/;

/**
 * Show every GetYourGuide gallery image at the main photo's rendition. Any
 * other URL — or a gallery whose main photo is not a GYG image — passes
 * through untouched.
 */
export function matchMainRendition(url: string, mainUrl: string | undefined): string {
  const main = mainUrl ? GYG_RENDITION.exec(mainUrl) : null;
  const img = GYG_RENDITION.exec(url);
  if (!main || !img || img[2] === main[2]) return url;
  return `${img[1]}/${main[2]}.jpg`;
}

/**
 * The "E-Ticket: Accepted" chip the service used to INFER from
 * `mobile_voucher: true`. That flag only says a voucher may be shown on a
 * phone — Bridgify sends no explicit e-ticket value on the product (its
 * `ticket_delivered_by` is null everywhere), and the real delivery type comes
 * later from the booking flow's `shipment-method` step.
 *
 * The service no longer emits it, but documents synced before that change
 * still carry it, so it is dropped here too. This exact shape was only ever
 * produced by the inference; an explicit `ticket_delivered_by` value arrives
 * labelled "Ticket Type" and is kept.
 */
/** The service's key for GetYourGuide pickup instructions (`mapInfoFields`). */
const PICKUP_POINT_KEY = 'pickup-point';

function isInferredETicket(f: { key: string; label: string; value: string }): boolean {
  return f.key === 'ticket-type' && f.label.trim() === 'E-Ticket' && f.value.trim() === 'Accepted';
}

function toItinerary(raw: AttractionRecord['itinerary']): AttractionItinerary | null {
  const stops = (raw?.stops ?? []).filter((s) => s.description?.trim());
  if (!raw || stops.length === 0) return null;
  return { start: raw.start || null, stops, end: raw.end || null };
}

/**
 * Venue Information block for the Opening Times section (BCP-2066 §3.4).
 *
 * Bridgify's `venue_information` field is sent by NO supplier (verified live
 * 2026-10-01 across List + Detail for GetYourGuide, HotelBeds, Tiqets, Viator,
 * Tillo), so the backend block never exists and the section showed no venue at
 * all. The venue's address — the top-level `address`, on ~70% of products — is
 * the only venue data the API carries, so it fills the block. A supplier-sent
 * block still wins when one arrives.
 */
function withVenueInformation(blocks: AttractionScheduleBlock[], address: string | undefined): AttractionScheduleBlock[] {
  const venue = address?.trim().replace(/[\s,]+$/, '');
  if (!venue || blocks.some((b) => b.key === 'venue-information')) return blocks;
  return [...blocks, { key: 'venue-information', label: 'Venue Information', icon: 'pin', rows: [], lines: [venue] }];
}

export function toDetail(record: AttractionRecord): AttractionDetail {
  const summary = toSummary(record);
  /*
   * NO placeholder fallback. BCP-2065 §2.2 hides the gallery section entirely
   * when a product has no images, and substituting `no-image.png` here made
   * that unreachable — every product rendered a one-tile gallery of a grey
   * placeholder. The section now decides from an honestly-empty list.
   */
  const gallery = unique(
    [record.mainPhotoUrl ?? '', ...(record.photos ?? [])].map((u) => matchMainRendition(u, record.mainPhotoUrl)),
  );

  /*
   * The Overview prefers `fullDescription` (the supplier's long-form copy) and
   * falls back to `description` (a one-line teaser, and all the card ever
   * showed). Both go through `stripHtml` — 56% of the catalogue sends rich
   * text, and this page renders prose as text, never as HTML.
   */
  const overviewSource = record.fullDescription?.trim() ? record.fullDescription : record.description;

  const fields: AttractionInfoField[] = (record.infoFields ?? [])
    // Belt-and-braces against a mirrored document that predates the mapper's
    // own empty-value guard: a chip with no value is a blank row on the page.
    .filter((f) => f.label?.trim() && f.value?.trim())
    .filter((f) => !isInferredETicket(f))
    .map((f) => ({
      key: f.key,
      label: f.label,
      value: f.value,
      icon: fieldIcon(f.icon),
      ...(f.mapsQuery ? { mapsQuery: f.mapsQuery } : {}),
    }));

  return {
    ...summary,
    gallery,
    // Pickup Point is instructions prose, so it gets its own tab section.
    infoFields: fields.filter((f) => f.key !== PICKUP_POINT_KEY),
    pickupPoint: fields.find((f) => f.key === PICKUP_POINT_KEY) ?? null,
    overview: paragraphs(stripHtml(overviewSource)),
    highlights: record.highlights ?? [],
    included: record.includedList ?? [],
    notIncluded: record.excludedList ?? [],
    scheduleBlocks: withVenueInformation(
      (record.scheduleBlocks ?? [])
        .map((b) => ({
          key: b.key,
          label: b.label,
          icon: fieldIcon(b.icon),
          rows: b.rows ?? [],
          lines: b.lines ?? [],
        }))
        // A block with neither grid nor prose renders as a bare sub-heading.
        .filter((b) => b.rows.length > 0 || b.lines.length > 0),
      record.address,
    ),
    // Same rich-text treatment: a handful of live records carry markup in the
    // policy/info prose too, and both render as plain text.
    additionalInformation: lines(stripHtml(record.importantInformation)),
    cancellationPolicy: lines(stripHtml(record.cancellationPolicy)),
    // Bridgify exposes no FAQ field on any endpoint (verified 2026-09-22
    // across a 300-product sample), so this section never renders today. The
    // page keeps the accordion so populating this is a data change, not a
    // rebuild.
    faqs: [],
    reviews: (record.reviews ?? []).map((r) => ({
      ...r,
      // Supplier review text can carry markup too, and it renders as text.
      text: stripHtml(r.text),
    })),
    freeCancellation: record.freeCancellation ?? false,
    instructions: record.instructions ?? [],
    landmarks: record.landmarks ?? [],
    // Stops without text would render as empty timeline rows.
    itinerary: toItinerary(record.itinerary),
    locationAddress: record.address ?? '',
    latitude: record.geo?.latitude ?? 0,
    longitude: record.geo?.longitude ?? 0,
    // Ticket shapes are NOT catalogue data — see `booking-shape.ts`.
    ticketOptions: [],
    slots: [],
    tiers: [],
  };
}
